import React, { FC, Suspense } from 'react'
import { Loading, Paragraph, TitleController } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { DefectNoticeSuccess } from '../Success'
import { ErrorHandler } from '../DefectNoticeErrorHandler'
import { EditDefectNoticeDataLoader } from './DefectNoticeEditViewDataLoader'
import { DefectNoticeEvents } from './components/DefectNoticeEvents/DefectNoticeEvents'
import { DefectNoticeInfo } from './components/DefectNoticeInfo/DefectNoticeInfo'
import { DefectNoticeForm } from '../Form'

import { View } from 'components'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

export type DefectNoticeViewState = 'form' | 'preview' | 'success'

export const DefectNoticeEditView: FC = () => {
  const [view, setView] = React.useState<DefectNoticeViewState>('form')
  const { t } = useTranslation(['common', 'defectNotice', 'titles'])
  const { defectNoticePath } = useContractPaths()

  return (
    <TitleController title={t('titles:defectNotice')}>
      <View>
        <View.Header>
          <View.HeaderLink to={defectNoticePath}>
            {t('common:views.defectNotice')}
          </View.HeaderLink>
          <View.HeaderTitle>
            {t('defectNotice:headings.defectNoticeEdit')}
          </View.HeaderTitle>
        </View.Header>
        <View.Sidebar />
        <View.Content>
          <Suspense fallback={<Loading variant="details" />}>
            <ErrorHandler>
              <EditDefectNoticeDataLoader>
                {({ defectNotice, ...data }) => {
                  // A notice that a Kenno user has changed to an observation stays
                  // visible to the resident, but core no longer accepts updates to it,
                  // so the form is left out entirely.
                  const isEditable = defectNotice.noticeType === 'defect_notice'

                  return (
                    <>
                      {view === 'success' && <DefectNoticeSuccess editMode />}
                      {view === 'form' && (
                        <>
                          <DefectNoticeInfo defectNotice={defectNotice} />
                          {!isEditable && (
                            <Paragraph className="mt-6">
                              {t('defectNotice:texts.notEditable')}
                            </Paragraph>
                          )}
                          <DefectNoticeEvents events={defectNotice.events} />
                        </>
                      )}
                      {view !== 'success' && isEditable && (
                        <DefectNoticeForm
                          {...data}
                          defectNotice={defectNotice}
                          view={view}
                          setView={setView}
                        />
                      )}
                    </>
                  )
                }}
              </EditDefectNoticeDataLoader>
            </ErrorHandler>
          </Suspense>
        </View.Content>
      </View>
    </TitleController>
  )
}
